import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ComponentType,
  type SVGProps,
} from 'react';

// Icon button compatto per azioni di riga nelle liste admin.
// Usa i token di brand: --primary-2 (azione), --danger (distruttiva), --soft (hover bg).
// Tooltip via attributo `title` nativo (accessibile, no librerie extra).

export type IconButtonTone = 'neutral' | 'primary' | 'danger';
export type IconButtonSize = 'sm' | 'md';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

interface CommonProps {
  icon: IconComponent;
  /** Etichetta accessibile + tooltip nativo. Obbligatoria. */
  label: string;
  tone?: IconButtonTone;
  size?: IconButtonSize;
}

type ButtonOnly = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'title'> & {
  as?: 'button';
  href?: never;
};

type AnchorOnly = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'aria-label' | 'title'> & {
  as: 'a';
  href: string;
};

export type IconButtonProps = CommonProps & (ButtonOnly | AnchorOnly);

const SIZE_PX: Record<IconButtonSize, { box: number; icon: number }> = {
  sm: { box: 26, icon: 14 },
  md: { box: 30, icon: 16 },
};

function toneColor(tone: IconButtonTone): string {
  switch (tone) {
    case 'danger':
      return 'var(--danger)';
    case 'primary':
      return 'var(--primary-2)';
    default:
      return 'var(--mute)';
  }
}

function toneHoverBg(tone: IconButtonTone): string {
  switch (tone) {
    case 'danger':
      return 'color-mix(in oklab, var(--danger) 12%, transparent)';
    case 'primary':
      return 'color-mix(in oklab, var(--primary-2) 14%, transparent)';
    default:
      return 'var(--soft)';
  }
}

export const IconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, IconButtonProps>(
  function IconButton(props, ref) {
    const { icon: Icon, label, tone = 'primary', size = 'sm', className, style, ...rest } = props;

    const { box, icon } = SIZE_PX[size];
    const color = toneColor(tone);
    const hoverBg = toneHoverBg(tone);

    const baseClass = [
      'audiq-icon-btn',
      'inline-flex items-center justify-center rounded-md',
      'transition-colors duration-100',
      'border border-transparent',
      'disabled:opacity-40 disabled:cursor-not-allowed',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ');

    const mergedStyle = {
      width: box,
      height: box,
      color,
      ['--audiq-icon-hover-bg' as string]: hoverBg,
      ...style,
    } as React.CSSProperties;

    const iconEl = <Icon width={icon} height={icon} strokeWidth={1.75} aria-hidden="true" />;

    if (rest && (rest as AnchorOnly).as === 'a') {
      const { as: _as, ...anchorRest } = rest as AnchorOnly;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          aria-label={label}
          title={label}
          className={baseClass}
          style={mergedStyle}
          {...anchorRest}
        >
          {iconEl}
        </a>
      );
    }

    const { as: _as, ...buttonRest } = rest as ButtonOnly;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={(buttonRest.type as 'button' | 'submit' | 'reset' | undefined) ?? 'button'}
        aria-label={label}
        title={label}
        className={baseClass}
        style={mergedStyle}
        {...buttonRest}
      >
        {iconEl}
      </button>
    );
  },
);
